/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import ImgixWithPrioritizedLoading from 'js/components/ImgixWithPrioritizedLoading';

import { Link, Typography2 } from '@coursera/cds-core';
import { ArrowNextIcon } from '@coursera/cds-icons';

import { BasicCard } from 'bundles/cds-labs/components';

const IMGPARAMS = {
  quality: 80,
};

const MAX_IMAGE_HEIGHT = 300;

const styles = {
  link: css`
    text-decoration: none;
    width: 100%;
    height: 100%;
    flex-grow: 1;

    &:focus,
    &:active {
      text-decoration: none;
    }
  `,
  card: css`
    border: none;
    width: 100%;
    height: 100%;
    padding: var(--cds-spacing-100);
  `,
  titleContainer: css`
    display: inline-flex;
    align-items: center;
  `,
  mobileTitleContainer: css`
    display: flex;
    align-items: center;
    flex-grow: 1;
    justify-content: space-between;
  `,
  title: css`
    margin-right: var(--cds-spacing-100);
  `,
  icon: css`
    align-self: start;
    margin-top: 2px;
  `,
  imageContainer: css`
    height: 180px;
    overflow: hidden;
    border-radius: var(--cds-border-radius-100);
    margin-bottom: var(--cds-spacing-150);
  `,
  image: css`
    width: 100%;
    height: 100%;
    object-fit: cover;
  `,
  body: css`
    margin: var(--cds-spacing-100) var(--cds-spacing-300) 0 0;
  `,
  mobileCard: css`
    width: 100%;
    display: flex;
    flex-direction: row;
    align-items: center;
  `,
  mobileImage: css`
    height: 54px;
    width: 56px;
    margin: var(--cds-spacing-150);
    border-radius: var(--cds-border-radius-100);
    overflow: hidden;
    flex-shrink: 0;
  `,
  mobileArrow: css`
    margin: 0 var(--cds-spacing-200) 0 auto;
  `,
};

export type BucketCardProps = {
  imageUrl: string;
  title: string;
  body: string;
  url: string;
  isMobile?: boolean;
};

const BucketCard = ({ imageUrl, title, body, url, isMobile }: BucketCardProps) => {
  return (
    <Link href={url} css={styles.link}>
      <BasicCard css={isMobile ? styles.mobileCard : styles.card}>
        <div css={isMobile ? styles.mobileImage : styles.imageContainer}>
          <ImgixWithPrioritizedLoading
            src={imageUrl}
            alt=""
            imgParams={IMGPARAMS}
            maxHeight={MAX_IMAGE_HEIGHT}
            css={styles.image}
          />
        </div>
        <Typography2
          component="h2"
          variant={isMobile ? 'subtitleLarge' : 'titleSmall'}
          css={isMobile ? styles.mobileTitleContainer : styles.titleContainer}
        >
          <span css={styles.title}>{title}</span>
          <span css={styles.icon}>
            <ArrowNextIcon size="medium" color="default" css={isMobile ? styles.mobileArrow : undefined} />
          </span>
        </Typography2>

        {!isMobile && (
          <Typography2 component="p" variant="bodyPrimary" color="supportText" css={styles.body}>
            {body}
          </Typography2>
        )}
      </BasicCard>
    </Link>
  );
};

export default BucketCard;
